import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaShieldAlt,
  FaBookOpen,
  FaClipboardList,
  FaFileAlt,
  FaCopy,
  FaHardHat,
  FaLightbulb
} from 'react-icons/fa';
import SubCategoryCarousel from './SubCategoryCarousel';

const ShieldIcon = FaShieldAlt as unknown as React.FC<{ className?: string }>;
const BookIcon = FaBookOpen as unknown as React.FC<{ className?: string }>;
const ClipboardIcon = FaClipboardList as unknown as React.FC<{ className?: string }>;
const FileIcon = FaFileAlt as unknown as React.FC<{ className?: string }>;
const CopyIcon = FaCopy as unknown as React.FC<{ className?: string }>;
const HardHatIcon = FaHardHat as unknown as React.FC<{ className?: string }>;
const LightbulbIcon = FaLightbulb as unknown as React.FC<{ className?: string }>;

interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  sort_order: number;
  children: Category[];
}

interface DocumentResult {
  id: string;
  title: string;
  reference_code: string | null;
  category_name: string;
}

const API_BASE = '/api';

const TILE_COLORS: Record<string, string> = {
  policies: '#2c3e50',
  manuals: '#34495e',
  procedures: '#1a5276',
  forms: '#7d6608',
  templates: '#5b2c6f',
  'toolbox-talks': '#943126',
  'guidance-notes': '#6c5b3f', // pick whatever fits your palette
};

function tileColor(slug: string): string {
  return TILE_COLORS[slug] || '#555';
}

const TILE_ICONS: Record<string, React.ReactElement> = {
  policies: <ShieldIcon className="tile-icon" />,
  manuals: <BookIcon className="tile-icon" />,
  procedures: <ClipboardIcon className="tile-icon" />,
  forms: <FileIcon className="tile-icon" />,
  templates: <CopyIcon className="tile-icon" />,
  'toolbox-talks': <HardHatIcon className="tile-icon" />,
  'guidance-notes': <LightbulbIcon className="tile-icon" />,
};

const HOME_LAYOUT_AREA: Record<string, string> = {
  forms: 'forms',
  procedures: 'procedures',
  policies: 'policies',
  manuals: 'manuals',
  templates: 'templates',
  'toolbox-talks': 'toolbox',
};

function tileIcon(slug: string): React.ReactElement {
  return TILE_ICONS[slug] || <FileIcon className="tile-icon" />;
}

// these two hold the most volume, so they get the bigger tile treatment
const FEATURED_SLUGS = ['forms'];

function HomePage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentResult[]>([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE}/categories`)
      .then((res) => res.json())
      .then(setCategories)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeout = setTimeout(() => {
      setSearching(true);
      fetch(`${API_BASE}/documents/search-all?q=${encodeURIComponent(searchQuery)}`)
        .then((res) => res.json())
        .then((data) => setSearchResults(Array.isArray(data) ? data : []))
        .finally(() => setSearching(false));
    }, 300); // debounce so we're not firing a request on every keystroke

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const goToCategory = (id: string, name: string) => {
    navigate(`/category/${id}?name=${encodeURIComponent(name)}`);
  };

  const subTiles = categories
    .filter((c) =>
      c.slug === 'procedures' ||
      c.slug === 'toolbox-talks' ||
      c.slug === 'forms' ||
      c.slug === 'guidance-notes'
    )
    .flatMap((parent) =>
      parent.children.map((child) => ({ ...child, parentName: parent.name }))
    );

  function CategoryTile({ cat, onClick }: { cat: Category; onClick: () => void }) {
    const tileRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (tileRef.current) {
        tileRef.current.style.setProperty('--tile-bg-image', `url(/category-images/${cat.slug}.jpg)`);
        tileRef.current.style.setProperty('--tile-bg-color', tileColor(cat.slug));
      }
    }, [cat.slug]);

    return (
      <div className="tile" onClick={onClick}>
        <div ref={tileRef} className="tile-inner">
          {tileIcon(cat.slug)}
          <span className="tile-label">{cat.name}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="search-hero">
        <p className="search-hero-label">Galldris HSEQ Library</p>
        <input
          type="text"
          placeholder="Search all documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-hero-input"
        />

        {searchQuery.trim() && (
          <div className="search-results-dropdown">
            {searching && <p className="search-results-status">Searching...</p>}
            {!searching && searchResults.length === 0 && (
              <p className="search-results-status">No documents found.</p>
            )}
            {searchResults.map((doc) => (
              <div
                key={doc.id}
                className="search-result-row"
                onClick={() => window.open(`${API_BASE}/documents/${doc.id}/preview`, '_blank')}
              >
                <span className="doc-name">
                  {doc.reference_code && <span className="doc-code">{doc.reference_code}</span>}
                  {doc.title}
                </span>
                <span className="search-result-category">{doc.category_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2>Categories</h2>
        <div className="tile-grid tile-grid-bento">
          {categories.map((cat) => (
            <CategoryTile
              key={cat.id}
              cat={cat}
              onClick={() => goToCategory(cat.id, cat.name)}
            />
          ))}
        </div>

      <h2>Sub-Categories</h2>
        <SubCategoryCarousel>
          {subTiles.map((cat) => (
            <div
              key={cat.id}
              className="tile tile-small tile-carousel-item"
              onClick={() => goToCategory(cat.id, `${cat.parentName} - ${cat.name}`)}
            >
              <div className="tile-inner tile-inner-sub">
                {tileIcon(
                  cat.slug.startsWith('procedures') ? 'procedures' :
                  cat.slug.startsWith('forms') ? 'forms' :
                  cat.slug.startsWith('guidance-notes') ? 'guidance-notes' :
                  'toolbox-talks'
                )}
                <span className="tile-label">
                  {cat.parentName} - {cat.name}
                </span>
              </div>
            </div>
          ))}
        </SubCategoryCarousel>
    </div>
  );
}

export default HomePage;